<template>
  <div>
    <t-loader :loading="isBusy"></t-loader>
    <div class="card mb-0">
      <div class="card-header">
        <div class="d-flex justify-content-between">
          <div>
            <div>{{ title }}</div>
            <small class="text-capitalize">{{ withdrawId ? 'withdrawn' : 'all' }} donations</small>
          </div>
          <div>
            <!--<div class="float-right">Balance: {{ balance | formatMoney }}</div>-->
            <b-dropdown v-if="userId && (permissions.fetchNew || permissions.fetchAll || permissions.verifyAll)"
                        size="lg"
                        toggle-class="text-decoration-none" variant="link" no-caret dropleft>
              <template #button-content>
                <ion-icon name="menu" class="md-hydrated"></ion-icon>
              </template>
              <b-dropdown-item href="#" @click="fetchNewDonations" :disabled="fetching">
                Fetch new donations
              </b-dropdown-item>
              <b-dropdown-divider v-if="permissions.fetchAll || permissions.verifyAll"></b-dropdown-divider>
              <template v-if="permissions.fetchAll">
                <b-dropdown-item href="#" @click="fetchAllDonations" :disabled="fetching">
                  Fetch all donations <span class="text-muted">(expensive)</span>
                </b-dropdown-item>
              </template>
              <template v-if="permissions.fetchConversions">
                <b-dropdown-item href="#" @click="fetchConversions" :disabled="fetching">
                  Fetch money conversions
                </b-dropdown-item>
              </template>
              <template v-if="permissions.verifyAll">
                <b-dropdown-item href="#" @click="startVerificationProcess" :disabled="fetching">
                  Start verification process
                </b-dropdown-item>
              </template>
              <template v-if="permissions.withdrawAll">
                <b-dropdown-item href="#" @click="withdrawAll" :disabled="fetching">
                  Withdraw donations
                </b-dropdown-item>
              </template>
              <b-dropdown-divider v-if="permissions.fetchAll || permissions.verifyAll"></b-dropdown-divider>
              <template v-if="permissions.verifyFailed">
                <b-dropdown-item href="#" @click="startFailedVerificationProcess" :disabled="fetching">
                  Verify failed donations
                </b-dropdown-item>
              </template>
            </b-dropdown>
          </div>
        </div>
      </div>
      <div class="card-body">
        <div v-if="message" class="alert" :class="`alert-${messageType}`">
          {{ message }}
          <button type="button" class="close" @click="message = null">
            <span>&times;</span>
          </button>
        </div>
        <b-table
            show-empty
            :responsive="true"
            :items="myProvider"
            :busy.sync="isBusy"
            :fields="fields"
            :current-page="currentPage"
            :per-page="perPage"
            ref="table">
          <template v-slot:cell(id)="data">
            <a v-if="permissions.view" class="text-info"
               :href="`/admin/services/streamlabs/transaction/${data.item.id}`">{{ data.item.id }}</a>
            <template v-else>{{ data.item.id }}</template>
          </template>
          <template v-slot:cell(status)="data">
                <span class="badge badge-pill text-capitalize" :class="'badge-'+data.item.class">
                  <template v-if="data.item.status === 'withdrawn'">
                    <span
                        :title="data.item.transaction_id !== null ? `Handled in Transaction #${data.item.transaction_id}` : ''">Handled</span>
                    <a class="ml-1 text-light" v-if="permissions.view && data.item.withdrawal_id !== null"
                       :href="`/admin/services/streamlabs/transaction/${data.item.withdrawal_id}`">#{{
                        data.item.withdrawal_id
                      }}</a>
                  </template>
                  <template v-else>{{ data.item.status }}</template>
                </span>

          </template>
          <template v-slot:cell(type)="data" class="text-right">
                <span
                    v-bind:class="{ 'text-success': data.item.type === 'donation', 'text-info': data.item.type === 'withdrawal', 'text-warning': data.item.type === 'refund'}">
                  {{ data.item.type }}
                </span>
          </template>
          <template v-slot:cell(amount_after_fee)="data" class="text-right">
                <span v-if="data.item.status === 'pending' && data.item.type === 'donation'"
                      v-b-tooltip.hover.bottom="'This donation is awaiting PayPal verification.'"
                      style="font-size: large;vertical-align: middle;line-height: 0;" class="text-info">
                  <ion-icon name="information-circle-outline"></ion-icon>
                </span>
            <span v-else
                  v-bind:class="{ 'text-success': data.item.type === 'donation' && ['verified', 'withdrawn'].includes(data.item.status), 'text-info': data.item.type === 'withdrawal',    'text-warning': data.item.type === 'refund'}">
                    {{ data.item.amount_after_fee | formatMoney }}
                </span>
          </template>
          <template v-slot:cell(total)="data">
            {{ data.item.money | formatMoney }}
          </template>
          <template v-slot:cell(fee)="data">
            <template v-if="['verified', 'withdrawn'].includes(data.item.status)">{{
                data.item.fee_amount | formatMoney
              }}
            </template>
            <template v-else>—</template>
          </template>
          <template v-slot:cell(conversions)="data">
            <div v-for="conversion in data.item.conversions">
               <span :title="`Original: ${conversion.before_fees} Fee: ${conversion.fees}\n${conversion.rate}`"
                     class="text-info mr-2" style="font-size: 11px">
                    {{ conversion.amount }} {{ conversion.currency }}
                </span>
            </div>
          </template>
          <template v-slot:cell(created_at)="data">
            {{ formatDateTime(data.item.created_at) }}
          </template>
        </b-table>
        <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage" aria-controls="table">
        </b-pagination>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';

export default {
  name: 'StreamlabsTable',
  props: {
    userId: {
      type: Number,
      default: null,
    },
    withdrawId: {
      type: Number,
      default: null,
    },
    asAdmin: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: 'Streamlabs donations',
    },
  },
  data() {
    return {
      message: null,
      messageType: 'info',
      fetching: false,
      permissions: {
        view: false,
        fetchNew: false,
        fetchAll: false,
        verifyAll: false,
        fetchConversions: false,
        withdrawAll: false,
        verifyFailed: false,
      },
      totalRows: 1,
      currentPage: 1,
      perPage: 10,
      transactions: [],
      isBusy: false,
      defaultFields: [
        {key: 'id', label: '#'},
        {key: 'status', label: 'Status'},
        {key: 'type', label: 'Type'},
        {key: 'donor', label: 'Donor'},
        {key: 'amount_after_fee', label: 'Amount', thClass: 'text-right', tdClass: 'text-right'},
        {key: 'fee', label: 'PayPal fee', thClass: 'text-right', tdClass: 'text-right'},
        {key: 'total', label: 'Before fees', thClass: 'text-right', tdClass: 'text-right'},
      ],
    };
  },
  computed: {
    fields() {
      return this.defaultFields;
    },
  },
  methods: {
    timeSinceTimestamp(ts) {
      return moment(ts).fromNow();
    },
    formatDateTime(ts) {
      return moment(ts).format('YYYY-MM-DD HH:mm');
    },
    fetchNewDonations() {
      this.callAdministratorMethod('fetchNew');
    },
    fetchAllDonations() {
      this.callAdministratorMethod('fetchAll');
    },
    fetchConversions() {
      this.callAdministratorMethod('fetchConversions');
    },
    withdrawAll() {
      this.callAdministratorMethod('withdrawAll');
    },
    startVerificationProcess() {
      this.callAdministratorMethod('verifyAll');
    },
    startFailedVerificationProcess() {
      this.callAdministratorMethod('verifyFailed');
    },
    callAdministratorMethod(name) {
      if (this.fetching) {
        return;
      }

      this.fetching = true;

      axios.post(`/admin/services/streamlabs/transactions/${this.userId}/${name}`).then((response) => {
        this.message = response.data.message;
        this.messageType = response.data.count > 0 ? 'success' : 'info';
        this.refresh();
      }).catch((response) => {
        this.message = response.response.data.message;
        this.messageType = 'danger';
      }).finally(() => {
        this.fetching = false;
      });
    },
    myProvider(ctx) {
      let path = `/services/streamlabs/transactions`;
      if (this.userId) {
        path = `/admin${path}/${this.userId}`;
      }
      if (this.withdrawId) {
        path = `${path}/relatedTo/${this.withdrawId}`;
        if (this.asAdmin) {
          path = `/admin${path}`;
        }
      }

      const query = `?page=${ctx.currentPage}&size=${ctx.perPage}`;
      const url = path + query;
      let promise = axios.get(url);

      return promise.then((response) => {
        const root = response.data;
        const data = root.data;
        const meta = root.meta;
        const permissions = root.permissions;
        this.permissions = permissions || this.permissions;
        let administrator = this.permissions.fetchConversions;
        if (administrator) {
          this.defaultFields.push({key: 'conversions', label: 'Conversion'});
        }
        this.defaultFields.push({key: 'created_at', label: administrator ? 'Created at' : 'Donated at'});

        this.currentPage = meta.current_page;
        this.totalRows = meta.total;
        this.transactions = data || [];

        return this.transactions;
      }).catch(error => {
        return [];
      });
    },
    refresh() {
      this.$nextTick(() => {
        this.$refs.table.refresh();
      });
    },
  },
};
</script>

<style lang="scss" scoped>
</style>
